'use client'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { useEffect } from 'react'

import { useSession } from '@/shared/lib'

const botPattern =
  '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)'

const regex = new RegExp(botPattern, 'i')

const conditionalSampleRate = regex.test(navigator.userAgent) ? 0 : 100

const getUidFromAirbridgeId = (airbridgeId: string) => {
  return airbridgeId
    .replace(/^[a-z]+-/i, '') // prefix 제거 (live-)
    .replace(/^0+/, '') // 앞쪽 0만 제거
}

export const DatadogRumInit = () => {
  const { data: session } = useSession()

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'live') {
      datadogRum.init({
        applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? '',
        clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
        site: 'datadoghq.com',
        service: 'samsam-web',
        env: process.env.NEXT_PUBLIC_ENVIRONMENT,
        version: process.env.NEXT_PUBLIC_DEPLOYMENT_ID, // EB 환경변수로 주입되는 값

        sessionSampleRate: conditionalSampleRate,
        sessionReplaySampleRate: 20,
        defaultPrivacyLevel: 'mask-user-input',
        plugins: [reactPlugin({ router: false })],

        allowedTracingUrls: [
          {
            match: 'https://api.33m2.co.kr',
            propagatorTypes: ['tracecontext'],
          },
          {
            match: 'https://web.33m2.co.kr',
            propagatorTypes: ['tracecontext'],
          },
        ],

        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
      })

      datadogLogs.init({
        clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        service: 'samsam-web',
        env: process.env.NEXT_PUBLIC_ENVIRONMENT,
        version: process.env.NEXT_PUBLIC_DEPLOYMENT_ID, // EB 환경변수로 주입되는 값
      })
    }
  }, [])

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'live') return

    if (session?.uid || session?.aid) {
      const id = session.uid
        ? String(session.uid)
        : getUidFromAirbridgeId(session.aid)

      datadogRum.setUser({
        id,
        name: session.name,
        isHost: session.isHost,
      })

      return
    }

    if (!session) {
      datadogRum.clearUser()
    }
  }, [session])

  return null
}
